<template>
    <v-container fluid class="page-aziende pa-4">

        <!-- Header -->
        <div class="page-header mb-4">
            <div class="header-title">
                <v-icon color="primary" size="22">mdi-domain</v-icon>
                <div class="titles">
                    <h1>Gestione Aziende</h1>
                    <p class="subtitle">Registrazioni e attivazione licenze</p>
                </div>
            </div>
        </div>

        <!-- KPI rapidi -->
        <v-row class="kpi-row mb-4" dense>
            <v-col cols="6" sm="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="warning">mdi-clock-outline</v-icon></div>
                    <div class="kpi-value">{{ totaleInAttesa }}</div>
                    <div class="kpi-label">In attesa</div>
                </v-card>
            </v-col>
            <v-col cols="6" sm="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="success">mdi-check-circle-outline</v-icon></div>
                    <div class="kpi-value">{{ totaleAttive }}</div>
                    <div class="kpi-label">Attive</div>
                </v-card>
            </v-col>
            <v-col cols="6" sm="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="error">mdi-cancel</v-icon></div>
                    <div class="kpi-value">{{ totaleSospese }}</div>
                    <div class="kpi-label">Sospese</div>
                </v-card>
            </v-col>
            <v-col cols="6" sm="3">
                <v-card class="kpi-card kpi-card--primary elevation-1">
                    <div class="kpi-top"><v-icon color="primary">mdi-domain</v-icon></div>
                    <div class="kpi-value">{{ aziende.length }}</div>
                    <div class="kpi-label">Totale</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- KPI abbonamenti in scadenza -->
        <v-row class="kpi-row mb-4" dense>
            <v-col cols="6" sm="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="error">mdi-alarm</v-icon></div>
                    <div class="kpi-value">{{ totaleInScadenza7 }}</div>
                    <div class="kpi-label">Scadenza entro 7gg</div>
                </v-card>
            </v-col>
            <v-col cols="6" sm="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="warning">mdi-calendar-alert</v-icon></div>
                    <div class="kpi-value">{{ totaleInScadenza30 }}</div>
                    <div class="kpi-label">Scadenza entro 30gg</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Card tabella -->
        <v-card class="elevation-2" rounded="lg">

            <!-- Toolbar -->
            <div class="toolbar px-4 py-3">
                <v-row dense align="center">
                    <v-col cols="12" sm="4">
                        <v-text-field v-model="ricerca" placeholder="Cerca azienda, P.IVA, email…" variant="outlined"
                            density="compact" clearable hide-details>
                            <template #prepend-inner><v-icon>mdi-magnify</v-icon></template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-select v-model="filtroStato" :items="opzioniStato" item-title="label" item-value="value"
                            label="Stato licenza" variant="outlined" density="compact" hide-details clearable />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-select v-model="filtroEmail"
                            :items="[{ label: 'Tutti', value: null }, { label: 'Email confermata', value: true }, { label: 'Email non confermata', value: false }]"
                            item-title="label" item-value="value" label="Conferma email" variant="outlined"
                            density="compact" hide-details clearable />
                    </v-col>
                    <v-col cols="auto" class="ms-auto">
                        <v-btn icon @click="caricaAziende" :loading="loading">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </div>

            <v-divider />

            <!-- Tabella -->
            <template v-if="loading">
                <div class="pa-4">
                    <v-skeleton-loader type="table" />
                </div>
            </template>

            <v-data-table v-else :headers="headers" :items="aziendeFiltrate" hover hide-default-footer
                :items-per-page="-1" no-data-text="Nessuna azienda trovata" class="aziende-table">

                <!-- Ragione sociale + codice -->
                <template v-slot:[`item.ragioneSociale`]="{ item }">
                    <div>
                        <div class="font-weight-semibold text-body-2">{{ item.ragioneSociale }}</div>
                        <div class="text-caption text-medium-emphasis">{{ item.codiceAzienda }}</div>
                    </div>
                </template>

                <!-- Email admin + conferma -->
                <template v-slot:[`item.emailAdmin`]="{ item }">
                    <div class="d-flex align-center gap-2">
                        <span class="text-body-2">{{ item.emailAdmin }}</span>
                        <v-tooltip :text="item.emailConfirmed ? 'Email verificata' : 'Email non ancora verificata'"
                            location="top">
                            <template #activator="{ props }">
                                <v-icon v-bind="props"
                                    :icon="item.emailConfirmed ? 'mdi-email-check' : 'mdi-email-alert'"
                                    :color="item.emailConfirmed ? 'success' : 'warning'" size="16" />
                            </template>
                        </v-tooltip>
                    </div>
                </template>

                <!-- Stato licenza chip -->
                <template v-slot:[`item.licenzaStato`]="{ item }">
                    <v-chip :color="chipColore(item.licenzaStato)" :text="chipLabel(item.licenzaStato)" size="small"
                        variant="tonal" />
                </template>

                <!-- Data registrazione -->
                <template v-slot:[`item.dataRegistrazione`]="{ item }">
                    <span class="text-body-2 text-medium-emphasis">{{ formatData(item.dataRegistrazione) }}</span>
                </template>

                <!-- Data attivazione -->
                <template v-slot:[`item.licenzaDataAttivazione`]="{ item }">
                    <span class="text-body-2 text-medium-emphasis">{{ formatData(item.licenzaDataAttivazione) }}</span>
                </template>

                <!-- Abbonamento: stato + scadenza colorata in base all'urgenza -->
                <template v-slot:[`item.abbonamento`]="{ item }">
                    <div v-if="!item.dataScadenza" class="text-caption text-grey">Nessun abbonamento</div>
                    <div v-else class="d-flex align-center gap-1">
                        <v-chip v-if="item.statoAbbonamento === 'prova'" size="x-small" color="info" variant="tonal"
                            class="me-1">In prova</v-chip>
                        <v-icon v-if="item.giorniAllaScadenza <= 7" size="16" color="error">mdi-alarm</v-icon>
                        <v-icon v-else-if="item.giorniAllaScadenza <= 30" size="16" color="warning">mdi-calendar-alert</v-icon>
                        <span class="text-body-2"
                            :class="{ 'text-error font-weight-bold': item.giorniAllaScadenza <= 7, 'text-warning-darken-2 font-weight-medium': item.giorniAllaScadenza > 7 && item.giorniAllaScadenza <= 30 }">
                            {{ formatData(item.dataScadenza) }}
                            <template v-if="item.giorniAllaScadenza < 0">(scaduto)</template>
                            <template v-else>({{ item.giorniAllaScadenza }}gg)</template>
                        </span>
                    </div>
                </template>

                <!-- Richeiste abbonamento pendenti -->
                <template v-slot:[`item.richiestePendenti`]="{ item }">
                    <v-badge v-if="item.richiestePendenti > 0" :content="item.richiestePendenti" color="error" inline />
                    <span v-else class="text-grey">—</span>
                </template>

                <!-- Azioni -->
                <template v-slot:[`item.azioni`]="{ item }">
                    <div class="d-flex align-center justify-end gap-1">

                        <!-- Attiva -->
                        <v-tooltip v-if="item.licenzaStato !== 1" text="Attiva licenza" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-check-circle-outline" color="success" variant="text"
                                    size="small" :disabled="!item.emailConfirmed" @click="apriConferma(item, 1)" />
                            </template>
                        </v-tooltip>

                        <!-- Sospendi -->
                        <v-tooltip v-if="item.licenzaStato === 1" text="Sospendi licenza" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-cancel" color="error" variant="text" size="small"
                                    @click="apriConferma(item, -1)" />
                            </template>
                        </v-tooltip>

                        <!-- Rimetti in attesa -->
                        <v-tooltip v-if="item.licenzaStato !== 0" text="Rimetti in attesa" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-clock-outline" color="warning" variant="text"
                                    size="small" @click="apriConferma(item, 0)" />
                            </template>
                        </v-tooltip>

                        <v-tooltip text="Gestisci abbonamento" location="top">
                            <template #activator="{ props }">
                                <v-btn v-bind="props" icon="mdi-package-variant-closed" color="primary" variant="text"
                                    size="small" @click="apriAbbonamento(item)" />
                            </template>
                        </v-tooltip>

                    </div>
                </template>

            </v-data-table>
        </v-card>

        <!-- Dialog conferma azione licenza -->
        <v-dialog v-model="dialogConferma" max-width="440">
            <v-card rounded="lg">
                <v-card-title class="d-flex align-center pa-4">
                    <v-icon :icon="iconaAzione" :color="coloreAzione" class="me-2" />
                    {{ titoloAzione }}
                </v-card-title>
                <v-card-text class="px-4 pb-2">
                    {{ testoAzione }}
                    <strong>{{ aziendaSelezionata?.ragioneSociale }}</strong>?
                    <template v-if="nuovoStato === 1">
                        <v-alert type="info" variant="tonal" density="compact" class="mt-3">
                            L'azienda riceverà una email di notifica e potrà iniziare ad usare il gestionale.
                        </v-alert>
                    </template>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn variant="text" @click="dialogConferma = false">Annulla</v-btn>
                    <v-btn :color="coloreAzione" variant="elevated" :loading="aggiornando" @click="confermaAzione">
                        Conferma
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" :timeout="3000">
            {{ snackbar.text }}
        </v-snackbar>

        <DialogAbbonamentoAdmin ref="dialogAbbonamento" @aggiornato="caricaAziende" />
    </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { aggiorna_licenza } from '@/api/api.registrazione'
import { get_aziende_con_abbonamento } from '@/api/api.abbonamento.js'
import DialogAbbonamentoAdmin from '@/components/admin/DialogAbbonamentoAdmin.vue'

export default {
    name: 'GestioneAziende',

    components: {
        DialogAbbonamentoAdmin
    },

    setup() {
        const loading = ref(false)
        const aggiornando = ref(false)
        const aziende = ref([])
        const ricerca = ref('')
        const filtroStato = ref(null)
        const filtroEmail = ref(null)

        const dialogConferma = ref(false)
        const aziendaSelezionata = ref(null)
        const nuovoStato = ref(null)

        const snackbar = ref({ show: false, text: '', color: 'success' })

        const opzioniStato = [
            { label: 'In attesa', value: 0 },
            { label: 'Attiva', value: 1 },
            { label: 'Sospesa', value: -1 }
        ]

        const headers = [
            { title: 'Azienda', value: 'ragioneSociale', sortable: true },
            { title: 'P.IVA', value: 'pIva', sortable: false, width: '130' },
            { title: 'Email admin', value: 'emailAdmin', sortable: false },
            { title: 'Stato licenza', value: 'licenzaStato', sortable: true, width: '130' },
            { title: 'Registrazione', value: 'dataRegistrazione', sortable: true, width: '140' },
            { title: 'Attivazione', value: 'licenzaDataAttivazione', sortable: true, width: '140' },
            { title: 'Abbonamento', value: 'abbonamento', sortable: false, width: '160' },
            { title: 'Utenti', value: 'numeroUtenti', sortable: true, width: '80', align: 'center' },
            { title: 'Richieste', value: 'richiestePendenti', sortable: true, width: '100', align: 'center' },
            { title: '', value: 'azioni', sortable: false, width: '120', align: 'end' },
        ]

        // KPI
        const totaleInAttesa = computed(() => aziende.value.filter(a => a.licenzaStato === 0).length)
        const totaleAttive = computed(() => aziende.value.filter(a => a.licenzaStato === 1).length)
        const totaleSospese = computed(() => aziende.value.filter(a => a.licenzaStato === -1).length)

        const inScadenzaEntro = (giorni) => aziende.value.filter(a =>
            (a.statoAbbonamento === 'attiva' || a.statoAbbonamento === 'prova') &&
            a.giorniAllaScadenza !== null && a.giorniAllaScadenza !== undefined &&
            a.giorniAllaScadenza >= 0 && a.giorniAllaScadenza <= giorni
        ).length

        const totaleInScadenza7 = computed(() => inScadenzaEntro(7))
        const totaleInScadenza30 = computed(() => inScadenzaEntro(30))

        const aziendeFiltrate = computed(() => {
            let lista = aziende.value
            const q = (ricerca.value || '').toLowerCase().trim()

            if (q) lista = lista.filter(a =>
                (a.ragioneSociale || '').toLowerCase().includes(q) ||
                (a.pIva || '').toLowerCase().includes(q) ||
                (a.emailAdmin || '').toLowerCase().includes(q) ||
                (a.codiceAzienda || '').toLowerCase().includes(q)
            )

            if (filtroStato.value !== null && filtroStato.value !== undefined)
                lista = lista.filter(a => a.licenzaStato === filtroStato.value)

            if (filtroEmail.value !== null && filtroEmail.value !== undefined)
                lista = lista.filter(a => a.emailConfirmed === filtroEmail.value)

            return lista
        })

        // Dialog conferma
        const iconaAzione = computed(() => {
            if (nuovoStato.value === 1) return 'mdi-check-circle-outline'
            if (nuovoStato.value === -1) return 'mdi-cancel'
            return 'mdi-clock-outline'
        })
        const coloreAzione = computed(() => {
            if (nuovoStato.value === 1) return 'success'
            if (nuovoStato.value === -1) return 'error'
            return 'warning'
        })
        const titoloAzione = computed(() => {
            if (nuovoStato.value === 1) return 'Attiva licenza'
            if (nuovoStato.value === -1) return 'Sospendi licenza'
            return 'Rimetti in attesa'
        })
        const testoAzione = computed(() => {
            if (nuovoStato.value === 1) return 'Vuoi attivare la licenza per '
            if (nuovoStato.value === -1) return 'Vuoi sospendere la licenza di '
            return 'Vuoi rimettere in attesa la licenza di '
        })

        const caricaAziende = async () => {
            loading.value = true
            try {
                const res = await get_aziende_con_abbonamento()
                aziende.value = res.data || []
            } catch {
                notifica('Errore nel caricamento delle aziende', 'error')
            } finally {
                loading.value = false
            }
        }

        const apriConferma = (azienda, stato) => {
            aziendaSelezionata.value = azienda
            nuovoStato.value = stato
            dialogConferma.value = true
        }

        const confermaAzione = async () => {
            if (!aziendaSelezionata.value) return
            aggiornando.value = true
            try {
                await aggiorna_licenza(aziendaSelezionata.value.aziendaId, nuovoStato.value)
                dialogConferma.value = false
                notifica('Licenza aggiornata con successo')
                await caricaAziende()
            } catch {
                notifica('Errore durante l\'aggiornamento', 'error')
            } finally {
                aggiornando.value = false
            }
        }

        const chipColore = (stato) => {
            if (stato === 1) return 'success'
            if (stato === -1) return 'error'
            return 'warning'
        }
        const chipLabel = (stato) => {
            if (stato === 1) return 'Attiva'
            if (stato === -1) return 'Sospesa'
            return 'In attesa'
        }

        const formatData = (d) => {
            if (!d) return '—'
            return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }

        const notifica = (text, color = 'success') => {
            snackbar.value = { show: true, text, color }
        }

        const dialogAbbonamento = ref(null)

        const apriAbbonamento = (azienda) => {
            dialogAbbonamento.value.apri(azienda)
        }

        onMounted(caricaAziende)

        return {
            loading, aggiornando, aziende, ricerca, filtroStato, filtroEmail,
            opzioniStato, headers, aziendeFiltrate,
            totaleInAttesa, totaleAttive, totaleSospese,
            totaleInScadenza7, totaleInScadenza30,
            dialogConferma, aziendaSelezionata, nuovoStato,
            iconaAzione, coloreAzione, titoloAzione, testoAzione,
            snackbar,
            caricaAziende, apriConferma, confermaAzione,
            chipColore, chipLabel, formatData,
            dialogAbbonamento,
            apriAbbonamento
        }
    }
}
</script>

<style scoped>
.page-aziende {
    max-width: 1300px;
    margin: auto;
}

/* KPI */
.kpi-card {
    padding: 14px 14px 12px;
    border-radius: 14px;
    background: white;
    border: 1px solid #eef0f3;
    position: relative;
}

.kpi-card--primary {
    box-shadow: 0 6px 18px rgba(30, 58, 95, .08);
    border: 1px solid rgba(30, 58, 95, .12);
}

.kpi-top {
    position: absolute;
    right: 12px;
    top: 10px;
    opacity: .8;
}

.kpi-value {
    font-size: 22px;
    font-weight: 800;
    color: #1E3A5F;
}

.kpi-label {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
}

/* Toolbar */
.toolbar {
    border-bottom: 1px solid #eee;
    background: #f9fafb;
}

/* Tabella */
.aziende-table :deep(thead th) {
    background: #f6f7f9 !important;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: .5px;
}

.aziende-table :deep(tbody tr:hover) {
    background: #f0f4f8 !important;
}
</style>